import React, { useState, useEffect } from "react";
import { Slideout, Button } from "@ann/web-components";
import { SlideoutContainer, SlideoutActions } from "./SlideoutTools.styled";
import InitialToolsView from "./InitialToolsView";

const SlideoutTools = ({
  open,
  onClose,
  onAddTools,
  onToolConfiguration,
  installedTools = [],
  availableTools = [],
  isLoading = false,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTools, setSelectedTools] = useState([]);
  const [selectedToolsCount, setSelectedToolsCount] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState("all");

  useEffect(() => {
    if (open) {
      setSearchQuery("");
      setSelectedTools([]);
      setSelectedToolsCount(0);
    }
  }, [open]);

  const handleSearchChange = (value) => {
    const searchValue =
      typeof value === "string" ? value : value?.target?.value || "";
    setSearchQuery(searchValue);
  };

  const handleResetSelections = () => {
    setSelectedTools([]);
    setSelectedToolsCount(0);
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
  };

  const handleToolSelection = (action_uuid) => {
    setSelectedTools((prev) => {
      const isSelected = prev.includes(action_uuid);
      if (isSelected) {
        setSelectedToolsCount((n) => n - 1);
        return prev.filter((id) => id !== action_uuid);
      } else {
        setSelectedToolsCount((n) => n + 1);
        return [...prev, action_uuid];
      }
    });
  };

  const handleToolConfigurationFromLibrary = (tool) => {
    if (onToolConfiguration) onToolConfiguration(tool, true);
  };

  const categories = [
    { id: "all", label: "All" },
    { id: "sales", label: "Sales" },
    { id: "marketing", label: "Marketing" },
    { id: "support", label: "Support" },
    { id: "conversations", label: "Conversations" },
    { id: "operations", label: "Operations" },
    { id: "research", label: "Research" },
    { id: "scheduling", label: "Scheduling" },
    { id: "service", label: "Service" },
    { id: "data", label: "Data" },
  ];

  const transformedAvailableTools = availableTools.map((tool) => {
    const id = tool.action_uuid || tool.tool_id;
    const name = tool.name || tool.title;
    const category = tool.category || tool.categories || '';
    
    return {
      action_uuid: id,
      tool_id: id,
      id: id,
      name: name,
      title: name,
      label: name,
      description: tool.description || '',
      type: tool.type || 'action',
      categories: Array.isArray(category) ? category : (category ? [category] : []),
      icon: tool.icon || '',
      tag: tool.tag || 'Ready to use',
      tagColor: tool.tagColor || 'informative',
      required_parameters: tool.required_parameters || []
    };
  });

  const getInstalledToolIds = () => {
    return installedTools.map((tool) => tool.id);
  };

  const filterTools = (tools) =>
    tools.filter((tool) => {
      const installedIds = getInstalledToolIds();
      const toolId = tool.id || tool.action_uuid || tool.tool_id;
      const isNotInstalled = !installedIds.includes(toolId);

      const cats = Array.isArray(tool.categories)
        ? tool.categories
        : (tool.categories ? [tool.categories] : []);
      const byCategory =
        selectedCategory === "all" || cats.includes(selectedCategory);

      const q = (searchQuery || "").toLowerCase();
      const text = [tool?.name, tool?.label, tool?.title, tool?.description]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      const bySearch = !q || text.includes(q);
      return isNotInstalled && byCategory && bySearch;
    });

  const filteredAvailableTools = filterTools(transformedAvailableTools);

  const tools = filteredAvailableTools.map((t) => ({
    ...t,
    id: t.id || t.action_uuid || t.tool_id,
    label: t.name || t.label,
    title: t.name || t.title,
    categories: Array.isArray(t.categories) ? t.categories : (t.categories ? [t.categories] : []),
  }));

  const handleAddToolsClick = () => {
    if (onAddTools && selectedTools.length > 0) {
      const selectedToolObjects = tools.filter(tool => 
        selectedTools.includes(tool.id)
      );
      onAddTools(selectedToolObjects);
    }
    handleResetSelections();
    onClose();
  };

  return (
    <Slideout open={open} onClose={onClose} size="medium">
      <SlideoutContainer>
        <InitialToolsView
          searchQuery={searchQuery}
          onSearchChange={handleSearchChange}
          selectedCategory={selectedCategory}
          onCategoryChange={handleCategoryChange}
          categories={categories}
          tools={tools}
          selectedTools={selectedTools}
          onToolSelection={handleToolSelection}
          onToolConfiguration={handleToolConfigurationFromLibrary}
        />

        <SlideoutActions>
          <Button
            label="Deselect all"
            size="medium"
            type="secondary"
            onClick={handleResetSelections}
            disabled={selectedToolsCount === 0}
          />
            <Button
              label={`Add (${selectedToolsCount})`}
              size="medium"
              type="primary"
              onClick={handleAddToolsClick}
              disabled={selectedToolsCount === 0}
            />
        </SlideoutActions>
      </SlideoutContainer>
    </Slideout>
  );
};

export default SlideoutTools;

